import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, Users, AlertTriangle, Check, X, Clock, Archive, Trash2, Download, UserPlus, Calendar, Moon, Sun } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { InfoTooltip } from '../components/ExplanationLayers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * COMMONS MANAGEMENT — DUAL STEWARD RULE
 * 
 * Per FINAL LOCKED DIRECTIVE:
 * - Every Commons MUST have: 1 Primary Steward + 1 Assistant Steward
 * - No irreversible actions (delete, archive, sponsor removal, export) without BOTH approvals
 * 
 * Single Steward Actions (allowed):
 * - Moderate content
 * - Edit Commons info
 * - Add content
 * - Manage members
 * 
 * Dual Approval Required:
 * - Delete Commons
 * - Archive Commons
 * - Remove sponsor
 * - Export data
 */
export default function CommonsManagement() {
  const { commonsId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [commons, setCommons] = useState(null);
  const [stewards, setStewards] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [showRequestAction, setShowRequestAction] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Seasonal Settings State
  const [seasonalEnabled, setSeasonalEnabled] = useState(false);
  const [seasonStart, setSeasonStart] = useState('');
  const [seasonEnd, setSeasonEnd] = useState('');
  const [isHibernating, setIsHibernating] = useState(false);
  const [seasonalSaving, setSeasonalSaving] = useState(false);

  useEffect(() => {
    if (commonsId) {
      fetchCommonsData();
    }
  }, [commonsId]);

  const fetchCommonsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [stewardsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/api/commons/${commonsId}/stewards`, { headers }),
        axios.get(`${API}/api/commons/${commonsId}/pending-actions`, { headers })
      ]);

      setStewards(stewardsRes.data);
      setPendingActions(actionsRes.data.pending_actions || []);
      
      // Load seasonal settings if available
      if (stewardsRes.data?.seasonal_settings) {
        const seasonal = stewardsRes.data.seasonal_settings;
        setSeasonalEnabled(seasonal.enabled || false);
        setSeasonStart(seasonal.season_start || '');
        setSeasonEnd(seasonal.season_end || '');
        setIsHibernating(seasonal.is_hibernating || false);
      }
    } catch (error) {
      console.error('Error fetching commons data:', error);
      toast.error('Failed to load Commons data');
    } finally {
      setLoading(false);
    }
  };

  const requestAction = async () => {
    if (!actionType) {
      toast.error('Please select an action type');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/commons/${commonsId}/request-action`, {
        commons_id: commonsId,
        action_type: actionType,
        reason: actionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message);
      setShowRequestAction(false);
      setActionType('');
      setActionReason('');
      fetchCommonsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request action');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (actionId, approved) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/commons/approve-action`, {
        action_id: actionId,
        approved: approved,
        reason: approved ? null : 'Rejected by steward'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message);
      fetchCommonsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  // Save Seasonal Settings
  const saveSeasonalSettings = async () => {
    setSeasonalSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Format dates as MM-DD for API
      const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };
      
      await axios.put(`${API}/api/commons/${commonsId}/seasonal-settings`, null, {
        params: {
          enabled: seasonalEnabled,
          season_start: formatDate(seasonStart),
          season_end: formatDate(seasonEnd)
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Seasonal settings saved');
      fetchCommonsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save seasonal settings');
    } finally {
      setSeasonalSaving(false);
    }
  };

  // Toggle hibernation manually
  const toggleHibernation = async () => {
    setSeasonalSaving(true);
    try {
      const token = localStorage.getItem('token');
      const newState = !isHibernating;
      
      // Update seasonal status in the database
      await axios.post(`${API}/api/commons/${commonsId}/toggle-hibernation`, {
        hibernating: newState
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsHibernating(newState);
      toast.success(newState ? 'Commons is now hibernating' : 'Commons is now active');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle hibernation');
    } finally {
      setSeasonalSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  const isSteward = stewards?.is_steward;
  const isPrimary = stewards?.primary_steward?.user_id === user?.user_id;
  const isAssistant = stewards?.assistant_steward?.user_id === user?.user_id;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:opacity-70 transition-opacity">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex items-center gap-1">
              <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Commons Stewardship</h1>
              <InfoTooltip topic="dual_steward" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Dual Steward Rule Banner */}
        <Card className="p-4" style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}>
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold mb-1">Dual Steward Governance</h3>
              <p className="text-sm opacity-90">
                Irreversible actions require approval from both Primary and Assistant Stewards.
              </p>
            </div>
          </div>
        </Card>

        {/* Stewards Section */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <div className="flex items-center gap-1 mb-4">
            <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Stewards</h3>
            <InfoTooltip topic="commons" />
          </div>
          
          <div className="space-y-4">
            {/* Primary Steward */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage)' }}>
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                  @{stewards?.primary_steward?.username || 'Unknown'}
                  {isPrimary && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}>You</span>}
                </p>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Primary Steward</p>
              </div>
            </div>

            {/* Assistant Steward */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-terra)' }}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                  @{stewards?.assistant_steward?.username || 'Not assigned'}
                  {isAssistant && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--lk-terra)', color: 'white' }}>You</span>}
                </p>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Assistant Steward</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Pending Actions */}
        {pendingActions.length > 0 && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--lk-terra)' }}>
              <AlertTriangle className="w-5 h-5" />
              Pending Actions ({pendingActions.length})
            </h3>
            
            <div className="space-y-3">
              {pendingActions.map(action => (
                <div key={action.action_id} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-background-primary)', border: '1px solid var(--fy-border-subtle)' }}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="font-medium capitalize" style={{ color: 'var(--fy-text-primary)' }}>
                        {action.action_type.replace('_', ' ')}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        Requested by {action.requested_by_role} steward
                      </p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--lk-terra)', color: 'white' }}>
                      Pending
                    </span>
                  </div>
                  
                  {action.reason && (
                    <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
                      Reason: {action.reason}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs mb-3" style={{ color: 'var(--fy-text-disabled)' }}>
                    <Clock className="w-3 h-3" />
                    Expires: {new Date(action.expires_at).toLocaleDateString()}
                  </div>
                  
                  {action.requires_your_approval && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproval(action.action_id, true)}
                        disabled={submitting}
                        size="sm"
                        className="flex-1"
                        style={{ backgroundColor: 'var(--lk-sage)' }}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        onClick={() => handleApproval(action.action_id, false)}
                        disabled={submitting}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  
                  {!action.requires_your_approval && (
                    <p className="text-xs italic" style={{ color: 'var(--fy-text-disabled)' }}>
                      Awaiting approval from other steward
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Governance Rules */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Governance Rules</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--lk-terra)' }}>
                Dual Approval Required
              </h4>
              <ul className="space-y-1">
                {stewards?.governance_rules?.dual_approval_required?.map((rule, i) => (
                  <li key={i} className="text-sm flex items-center gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                    <AlertTriangle className="w-3 h-3" style={{ color: 'var(--lk-terra)' }} />
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--lk-sage)' }}>
                Single Steward Actions
              </h4>
              <ul className="space-y-1">
                {stewards?.governance_rules?.single_steward_allowed?.map((rule, i) => (
                  <li key={i} className="text-sm flex items-center gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                    <Check className="w-3 h-3" style={{ color: 'var(--lk-sage)' }} />
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>

        {/* Seasonal Settings - Steward Only */}
        {isSteward && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }} data-testid="seasonal-settings">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5" style={{ color: 'var(--lk-earth-brown)' }} />
              <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Seasonal Settings</h3>
            </div>
            
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Configure seasonal dates to automatically hibernate the Commons outside active periods.
            </p>

            {/* Enable Seasonal Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg mb-4" style={{ backgroundColor: 'var(--fy-background-primary)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4" style={{ color: seasonalEnabled ? 'var(--lk-secondary-orange)' : 'var(--fy-text-disabled)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Enable Seasonal Mode</span>
              </div>
              <button
                onClick={() => setSeasonalEnabled(!seasonalEnabled)}
                className="w-12 h-6 rounded-full transition-colors relative"
                style={{ backgroundColor: seasonalEnabled ? 'var(--lk-primary-green)' : 'var(--fy-border-subtle)' }}
                data-testid="seasonal-toggle"
              >
                <span 
                  className="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform"
                  style={{ left: seasonalEnabled ? '28px' : '4px' }}
                />
              </button>
            </div>

            {/* Season Date Range */}
            {seasonalEnabled && (
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--fy-text-secondary)' }}>
                      Season Start
                    </label>
                    <input
                      type="date"
                      value={seasonStart}
                      onChange={(e) => setSeasonStart(e.target.value)}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                      data-testid="season-start-date"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--fy-text-secondary)' }}>
                      Season End
                    </label>
                    <input
                      type="date"
                      value={seasonEnd}
                      onChange={(e) => setSeasonEnd(e.target.value)}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{ backgroundColor: 'var(--fy-background-primary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                      data-testid="season-end-date"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={saveSeasonalSettings}
                  disabled={seasonalSaving}
                  size="sm"
                  className="w-full"
                  style={{ backgroundColor: 'var(--lk-primary-green)' }}
                  data-testid="save-seasonal-btn"
                >
                  {seasonalSaving ? 'Saving...' : 'Save Seasonal Dates'}
                </Button>
              </div>
            )}

            {/* Hibernation Toggle */}
            <div className="p-3 rounded-lg" style={{ backgroundColor: isHibernating ? 'rgba(107, 114, 128, 0.2)' : 'var(--fy-background-primary)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4" style={{ color: isHibernating ? '#6B7280' : 'var(--fy-text-disabled)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Hibernation Mode</span>
                </div>
                <button
                  onClick={toggleHibernation}
                  disabled={seasonalSaving}
                  className="w-12 h-6 rounded-full transition-colors relative"
                  style={{ backgroundColor: isHibernating ? '#6B7280' : 'var(--fy-border-subtle)' }}
                  data-testid="hibernation-toggle"
                >
                  <span 
                    className="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform"
                    style={{ left: isHibernating ? '28px' : '4px' }}
                  />
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                {isHibernating 
                  ? 'Commons is hibernating. No new posts or events allowed.'
                  : 'Commons is active. Toggle to manually hibernate outside seasonal dates.'}
              </p>
            </div>

            {/* Auto-hibernate info */}
            {seasonalEnabled && seasonStart && seasonEnd && (
              <p className="text-xs mt-3" style={{ color: 'var(--fy-text-disabled)' }}>
                Commons will auto-hibernate outside {new Date(seasonStart).toLocaleDateString()} - {new Date(seasonEnd).toLocaleDateString()}
              </p>
            )}
          </Card>
        )}

        {/* Request Action Button */}
        {isSteward && (
          <Button
            onClick={() => setShowRequestAction(true)}
            className="w-full"
            variant="outline"
            style={{ borderColor: 'var(--lk-terra)', color: 'var(--lk-terra)' }}
          >
            Request Governance Action
          </Button>
        )}

        {/* Request Action Modal */}
        {showRequestAction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <Card className="w-full max-w-md p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
                Request Governance Action
              </h3>
              
              <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
                This action requires approval from the other steward. It will expire in 7 days if not approved.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Action Type</label>
                  <select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    className="w-full mt-1 p-2 rounded-lg"
                    style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                  >
                    <option value="">Select action...</option>
                    <option value="archive">Archive Commons (read-only)</option>
                    <option value="export">Export all data</option>
                    <option value="sponsor_remove">Remove sponsor</option>
                    <option value="delete">Delete Commons (permanent)</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Reason (optional)</label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Why is this action needed?"
                    rows={3}
                    className="w-full mt-1 p-2 rounded-lg resize-none"
                    style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowRequestAction(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={requestAction}
                    disabled={submitting || !actionType}
                    className="flex-1"
                    style={{ backgroundColor: 'var(--lk-terra)' }}
                  >
                    {submitting ? 'Submitting...' : 'Request Action'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
